'use client';
import { createDeepResearch } from '@/app/services/deep-research';
import {
  DEEP_RESEARCH_DEFAULT_STYLE,
  DEEP_RESEARCH_STYLE_OPTIONS,
  normalizeDeepResearchStyle,
  type DeepResearchStyle,
} from '@/app/(protected)/deep-research/style-options';
import { insertSidebarDeepResearchHistory } from '@/components/sidebar-history/sidebar-history-cache';
import StartTaskModal from '@/components/start-task-modal';
import { AutoResizeTextarea } from '@/components/ui/auto-resize-textarea';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { deepResearchLangs } from '@/config/site';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useMaintenance } from '@/hooks/use-maintenance';
import { useNewsEventTracker } from '@/hooks/use-news-event-tracker';
import { useAuth } from '@/providers/auth-provider';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowUpIcon,
  FileTextIcon,
  LanguagesIcon,
  Loader2Icon,
} from 'lucide-react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';

const SearchTextarea = () => {
  const searchParams = useSearchParams();
  const q = searchParams.get('q');
  const searchRef = useRef<HTMLTextAreaElement>(null);
  const [loading, setLoading] = useState(false);
  const [currentLang, setCurrentLang] = useState(() => {
    if (typeof window === 'undefined') {
      return 'zh-cn';
    }
    try {
      const savedLang = localStorage.getItem('deep-research-currentLang');
      if (
        savedLang &&
        deepResearchLangs.find((item) => item.value === savedLang)
      ) {
        return savedLang;
      }
    } catch (error) {
      console.warn('Failed to load currentLang from localStorage:', error);
    }
    return 'zh-cn';
  });
  const [value, setValue] = useState(q || '');
  const [isPending, startTransition] = useTransition();
  const [style, setStyle] = useState<DeepResearchStyle>(() => {
    if (typeof window === 'undefined') {
      return DEEP_RESEARCH_DEFAULT_STYLE;
    }
    try {
      return normalizeDeepResearchStyle(
        localStorage.getItem('deep-research-style'),
      );
    } catch (error) {
      console.warn('Failed to load style from localStorage:', error);
    }
    return DEEP_RESEARCH_DEFAULT_STYLE;
  });

  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const currentLangRef = useRef(currentLang);
  const { user, triggerLogin } = useAuth();
  const { trackSearch, flushEvents } = useNewsEventTracker();
  const router = useRouter();
  const { fetchMaintenanceStatus, showMaintenance } = useMaintenance();
  const queryClient = useQueryClient();

  // 保存当前语言到 localStorage
  useEffect(() => {
    try {
      localStorage.setItem('deep-research-currentLang', currentLang);
    } catch (error) {
      console.warn('Failed to save currentLang to localStorage:', error);
    }
  }, [currentLang]);

  // 保存当前风格到 localStorage
  useEffect(() => {
    try {
      localStorage.setItem('deep-research-style', style);
    } catch (error) {
      console.warn('Failed to save style to localStorage:', error);
    }
  }, [style]);

  const onEnter = async () => {
    const query = searchRef.current?.value.trim();
    if (!query) {
      setLoading(false);
      return;
    }
    try {
      trackSearch(query);
      await flushEvents();
      const { data } = await createDeepResearch(query, currentLang, style);
      insertSidebarDeepResearchHistory(queryClient, {
        drid: data.drid,
        error_msg: '',
        point: data.point,
        status: 'INIT',
        uid: data.uid,
        user_query: query,
        ctime: new Date().toISOString(),
        share_id: null,
        share_to_square: false,
      });
      startTransition(() => {
        router.push(`/deep-research/${data.drid}`);
      });
    } catch {}
  };

  const onOpenModal = async () => {
    if (!user && triggerLogin) {
      setLoading(false);
      triggerLogin(() => {
        onOpen();
      });
      return;
    }
    onOpen();
    setLoading(false);
  };

  const onConfirm = async () => {
    setLoading(true);
    const maintenanceStatus = await fetchMaintenanceStatus('deep_research');

    if (maintenanceStatus && maintenanceStatus.is_maintenance) {
      showMaintenance(maintenanceStatus.title, maintenanceStatus.content);
      return;
    }
    onOpenModal();
  };

  // 简化的键盘事件处理
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        onConfirm();
      }
    },
    [onConfirm],
  );

  useEffect(() => {
    currentLangRef.current = currentLang;
  }, [currentLang]);

  // 缓存当前语言项，避免重复查找
  const currentModelItem = useMemo(() => {
    return deepResearchLangs.find((item) => item.value === currentLang);
  }, [currentLang]);
  const currentStyleItem = useMemo(() => {
    return DEEP_RESEARCH_STYLE_OPTIONS.find((item) => item.value === style);
  }, [style]);

  return (
    <div className="content-center relative max-w-[784px] mx-auto w-full">
      <div className="relative z-10 flex w-full flex-col bg-muted rounded-xl">
        <form className="p-3 z-20 relative rounded-2xl overflow-visible material-medium border border-neutral-200 dark:border-neutral-700 focus-within:border-neutral-400 focus-within:ring-2 ring-neutral-300 dark:ring-neutral-700 dark:focus-within:border-neutral-600 shadow-none bg-background dark:bg-neutral-900/80">
          <div className="flex flex-col">
            <AutoResizeTextarea
              ref={searchRef}
              id="search"
              placeholder="文献综述、选题探索、研究现状、课题材料"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              minHeight={44}
              maxHeight={208}
              className="w-full font-normal bg-transparent outline-hidden! placeholder:text-muted-foreground focus-visible:outline-hidden resize-none pt-0 transition-all duration-200! motion-reduce:transition-none text-sm border-0 ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-0"
            />
            <div className="flex items-center justify-between pt-2 gap-x-2">
              <div className="flex items-center gap-x-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="md:h-7 text-foreground text-xs"
                    >
                      <LanguagesIcon className="w-3.5! h-3.5!" />
                      {currentModelItem?.label}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-32" align="start">
                    <DropdownMenuLabel>输出结果</DropdownMenuLabel>
                    {deepResearchLangs.map((item) => (
                      <DropdownMenuCheckboxItem
                        checked={currentLang === item.value}
                        key={item.value}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setCurrentLang(item.value);
                            searchRef.current?.focus();
                          }
                        }}
                        className="gap-y-0.5"
                      >
                        {item.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="md:h-7 text-foreground text-xs"
                    >
                      <FileTextIcon className="w-3.5! h-3.5!" />
                      {currentStyleItem?.label ?? '学术综述'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-44" align="start">
                    <DropdownMenuLabel>报告类型</DropdownMenuLabel>
                    {DEEP_RESEARCH_STYLE_OPTIONS.map((item) => (
                      <DropdownMenuCheckboxItem
                        checked={style === item.value}
                        key={item.value}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setStyle(item.value);
                            searchRef.current?.focus();
                          }
                        }}
                        className="gap-y-0.5"
                      >
                        {item.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex-1"></div>
              <Button
                disabled={loading || !value?.trim() || isPending}
                size="icon"
                color="primary"
                aria-label="开始研究"
                className="w-8 h-8"
                onClick={(e) => {
                  e.preventDefault();
                  onConfirm();
                }}
              >
                {loading || isPending ? (
                  <Loader2Icon className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowUpIcon className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>

      {user && !user.membership && (
        <div className="px-3 py-2 text-xs flex items-center gap-x-2 bg-accent rounded-b-2xl -mt-3 pt-5">
          <span className="text-muted-foreground">
            深度研究：轻量版会员特惠，赠送3000积分/月，仅需9.9元。
          </span>
          <Link href="/pricing/subscribe" className="text-primary">
            立即升级
          </Link>
        </div>
      )}

      <StartTaskModal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        title="深度研究"
        offsetPoint={0}
        loading={loading || isPending}
        onStart={async () => {
          setLoading(true);
          await onEnter();
          setLoading(false);
        }}
      />
    </div>
  );
};

export default SearchTextarea;
